import { Injectable } from '@angular/core';
import {
  NativeBiometric,
  BiometryType,
} from '@capgo/capacitor-native-biometric';
import { Preferences } from '@capacitor/preferences';
import { Capacitor } from '@capacitor/core';
import CryptoES from 'crypto-es';

export enum BiometricErrorCode {
  UNKNOWN_ERROR = 0,
  BIOMETRICS_UNAVAILABLE = 1,
  USER_LOCKOUT = 2,
  BIOMETRICS_NOT_ENROLLED = 3,
  USER_TEMPORARY_LOCKOUT = 4,
  AUTHENTICATION_FAILED = 10,
  APP_CANCEL = 11,
  INVALID_CONTEXT = 12,
  NOT_INTERACTIVE = 13,
  PASSCODE_NOT_SET = 14,
  SYSTEM_CANCEL = 15,
  USER_CANCEL = 16,
  USER_FALLBACK = 17,
}

@Injectable({
  providedIn: 'root',
})
export class BiometricService {
  private readonly CREDENTIALS_KEY = 'biometric_credentials';
  private readonly BIOMETRIC_ENABLED_KEY = 'biometric_enabled';
  // WICHTIG: Gleicher Key überall! Unterschiedliche Keys pro Environment brechen
  // bestehende Credentials wenn User zwischen Dev/Test/Prod wechselt.
  // Der Key ist nur für lokale Obfuscation - Server-Auth via JWT ist die echte Sicherheit.
  private readonly ENCRYPTION_KEY = 'FixPlanerBio2024!';
  private readonly SERVER_NAME = 'fixplaner-app';

  constructor() {}

  async isBiometricAvailable(): Promise<boolean> {
    if (!Capacitor.isNativePlatform()) {
      return false;
    }

    try {
      const result = await NativeBiometric.isAvailable({ useFallback: true });
      return result.isAvailable;
    } catch (error) {
      console.error('Biometric check failed:', error);
      return false;
    }
  }

  async getBiometryType(): Promise<BiometryType | undefined> {
    try {
      const result = await NativeBiometric.isAvailable({ useFallback: true });
      return result.biometryType;
    } catch (error) {
      console.error('Get biometry type failed:', error);
      return undefined;
    }
  }

  async getBiometricErrorCode(): Promise<number | undefined> {
    try {
      const result = await NativeBiometric.isAvailable({ useFallback: false });
      return result.errorCode;
    } catch (error) {
      console.error('Get biometric error code failed:', error);
      return undefined;
    }
  }

  async authenticate(reason?: string): Promise<boolean> {
    if (!(await this.isBiometricAvailable())) {
      return false;
    }

    try {
      await NativeBiometric.verifyIdentity({
        reason: reason || 'Bitte authentifizieren Sie sich für FixPlaner',
        title: 'Authentifizierung',
        subtitle: 'Zugang zu FixPlaner',
        description: 'Verwenden Sie Ihre Biometrie für die sichere Anmeldung',
        fallbackTitle: 'Passcode verwenden',
        negativeButtonText: 'Abbrechen',
        useFallback: true,
        maxAttempts: 3,
      });
      return true;
    } catch (error: any) {
      console.error('Biometric authentication failed:', error);

      if (error.code) {
        switch (error.code) {
          case BiometricErrorCode.USER_CANCEL:
            break;
          case BiometricErrorCode.AUTHENTICATION_FAILED:
            break;
          case BiometricErrorCode.USER_LOCKOUT:
          case BiometricErrorCode.USER_TEMPORARY_LOCKOUT:
            break;
          default:
        }
      }
      return false;
    }
  }

  // ÜBERARBEITET: Mit RememberMe Support
  async saveCredentialsWithRememberMe(
    emailOrUsername: string,
    password: string,
    isTerminalMode: boolean,
    rememberMe: boolean = false
  ): Promise<void> {
    try {
      if (!(await this.isBiometricAvailable())) {
        throw new Error('Biometric not available');
      }

      await NativeBiometric.setCredentials({
        username: emailOrUsername,
        password: password,
        server: this.SERVER_NAME,
      });

      const metadata = {
        isTerminalMode,
        rememberMe,
        timestamp: new Date().toISOString(),
      };

      const encrypted = CryptoES.AES.encrypt(
        JSON.stringify(metadata),
        this.ENCRYPTION_KEY
      ).toString();

      await Preferences.set({
        key: this.CREDENTIALS_KEY,
        value: encrypted,
      });

      await this.setBiometricEnabled(true);
    } catch (error) {
      console.error('Failed to save credentials:', error);
      throw error;
    }
  }

  // Legacy Methode für Kompatibilität
  async saveCredentials(
    emailOrUsername: string,
    password: string,
    isTerminalMode: boolean
  ): Promise<void> {
    return this.saveCredentialsWithRememberMe(
      emailOrUsername,
      password,
      isTerminalMode,
      false
    );
  }

  // ÜBERARBEITET: Mit RememberMe Support
  async getCredentials(): Promise<{
    emailOrUsername: string;
    password: string;
    isTerminalMode: boolean;
    rememberMe: boolean;
  } | null> {
    try {
      const nativeCredentials = await NativeBiometric.getCredentials({
        server: this.SERVER_NAME,
      });

      if (!nativeCredentials) {
        return null;
      }

      const { value } = await Preferences.get({ key: this.CREDENTIALS_KEY });

      let isTerminalMode = false;
      let rememberMe = false;

      if (value) {
        try {
          const decrypted = CryptoES.AES.decrypt(value, this.ENCRYPTION_KEY);
          const decryptedString = decrypted.toString(CryptoES.enc.Utf8);

          if (decryptedString) {
            const metadata = JSON.parse(decryptedString);
            isTerminalMode = metadata.isTerminalMode || false;
            rememberMe = metadata.rememberMe || false;
          }
        } catch (error) {
          console.error('Failed to decrypt metadata:', error);
        }
      }

      return {
        emailOrUsername: nativeCredentials.username,
        password: nativeCredentials.password,
        isTerminalMode,
        rememberMe,
      };
    } catch (error: any) {
      console.error('Failed to get credentials:', error);

      return null;
    }
  }

  async getCredentialsWithBiometric(): Promise<{
    emailOrUsername: string;
    password: string;
    isTerminalMode: boolean;
    rememberMe: boolean;
  } | null> {
    try {
      const isAuthenticated = await this.authenticate(
        'Bitte authentifizieren Sie sich für die Anmeldung'
      );

      if (!isAuthenticated) {
        return null;
      }

      return await this.getCredentials();
    } catch (error) {
      console.error('Failed to get credentials with biometric:', error);
      return null;
    }
  }

  async clearCredentials(): Promise<void> {
    try {
      await NativeBiometric.deleteCredentials({
        server: this.SERVER_NAME,
      });
    } catch (error) {
      console.error('Failed to delete native credentials:', error);
    }

    await Preferences.remove({ key: this.CREDENTIALS_KEY });
    await this.setBiometricEnabled(false);
  }

  async isBiometricEnabled(): Promise<boolean> {
    const { value } = await Preferences.get({
      key: this.BIOMETRIC_ENABLED_KEY,
    });
    return value === 'true';
  }

  async setBiometricEnabled(enabled: boolean): Promise<void> {
    await Preferences.set({
      key: this.BIOMETRIC_ENABLED_KEY,
      value: enabled.toString(),
    });
  }

  getBiometryTypeName(type: BiometryType | undefined): string {
    if (type === undefined || type === null) {
      return 'Biometrie';
    }

    switch (type) {
      case BiometryType.NONE:
        return 'Keine Biometrie';
      case BiometryType.TOUCH_ID:
        return 'Touch ID';
      case BiometryType.FACE_ID:
        return 'Face ID';
      case BiometryType.FINGERPRINT:
        return 'Fingerabdruck';
      case BiometryType.FACE_AUTHENTICATION:
        return 'Gesichtserkennung';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'Iris-Scan';
      case BiometryType.MULTIPLE:
        return 'Biometrie';
      default:
        return 'Biometrie';
    }
  }

  getErrorMessage(errorCode: number): string {
    switch (errorCode) {
      case BiometricErrorCode.BIOMETRICS_UNAVAILABLE:
        return 'Biometrie ist auf diesem Gerät nicht verfügbar';
      case BiometricErrorCode.USER_LOCKOUT:
        return 'Zu viele fehlgeschlagene Versuche. Biometrie ist gesperrt';
      case BiometricErrorCode.BIOMETRICS_NOT_ENROLLED:
        return 'Keine biometrischen Daten eingerichtet. Bitte in den Geräteeinstellungen konfigurieren';
      case BiometricErrorCode.USER_TEMPORARY_LOCKOUT:
        return 'Zu viele Versuche. Bitte warten Sie 30 Sekunden';
      case BiometricErrorCode.AUTHENTICATION_FAILED:
        return 'Authentifizierung fehlgeschlagen';
      case BiometricErrorCode.USER_CANCEL:
        return 'Authentifizierung abgebrochen';
      case BiometricErrorCode.PASSCODE_NOT_SET:
        return 'Kein Passcode eingerichtet';
      default:
        return 'Biometrische Authentifizierung fehlgeschlagen';
    }
  }
}
